import { Link } from "react-router-dom";
import { Award, Users, Film, MapPin, Heart, CheckCircle2, Clapperboard, PenLine, Camera, Scissors, Send } from "lucide-react";
import Reveal from "../components/Reveal";
import ClientMarquee from "../components/ClientMarquee";
import BtsGallery from "../components/BtsGallery";
import SEO from "../components/SEO";
import { SITE, TESTIMONIALS } from "../data/content";

const SITE_URL = "https://yellowvideoproduction.com";

// Team roster. Photos live at /public/team/{slug}.jpg (portrait 800x1000).
const TEAM = [
  {
    slug: "dave",
    name: "Dave Roberts",
    role: "Head of Strategy",
    lead: "In charge of creative direction",
    bio:
      "Dave leads on strategy and creative direction at Yellow. He works with every new client to shape briefs into ideas, plotting out the story, tone and structure of each film before a camera is switched on. With more than a decade of experience building campaigns for national brands, universities and public-sector bodies, Dave is the person who makes sure every video Yellow delivers has a job to do — and does it.",
    initials: "DR",
    photo: "/team/dave.jpg",
    linkedin: "https://www.linkedin.com/in/daveyellow/",
  },
  {
    slug: "shaun",
    name: "Shaun Bishop",
    role: "Head of Production",
    lead: "In charge of planning & filming",
    bio:
      "Shaun runs the production side of Yellow — schedules, kit lists, locations, crew and, on the day, the camera. From single-camera testimonial shoots to multi-day brand campaigns with talent, drones and stills, Shaun is the calm hand who makes sure filming days run to plan. Broadcast-quality output, without the broadcast-agency chaos.",
    initials: "SB",
    photo: "/team/shaun.jpg",
    linkedin: "https://www.linkedin.com/in/yellowshaun/",
  },
  {
    slug: "keith",
    name: "Keith Lupton",
    role: "Head of Photography and Design",
    lead: "In charge of stills photography and graphic design",
    bio:
      "Keith is Yellow's lead photographer and designer. Alongside every video shoot he captures the stills our clients get for FREE — headshots, product, event and location photography. He also designs graphic assets that sit alongside the film work: thumbnails, brochure imagery, campaign artwork and identity design. A CAA-licensed drone pilot, too, when the story needs to be told from the air.",
    initials: "KL",
    photo: "/team/keith.jpg",
    linkedin: "https://www.linkedin.com/in/keith-lupton-01893570/",
  },
  {
    slug: "scott",
    name: "Scott Bishop",
    role: "Head of Post Production and Motion Graphics",
    lead: "In charge of editing, motion graphics and CGI",
    bio:
      "Scott turns raw footage into the finished films you see on the showreel. Editing, colour grading, sound design, motion graphics and full CGI all sit under his roof. He's the one making sure the pace, tone and style of your videos match the story of your brand — and that every cutdown for social, web and broadcast lands right.",
    initials: "SB",
    photo: "/team/scott.jpg",
    linkedin: "https://www.linkedin.com/in/scott-b-06593570/",
  },
];

const NUMBERS = [
  { value: "1,000+", label: "Films delivered", icon: Film },
  { value: "12+", label: "Years operating", icon: Award },
  { value: "100+", label: "Brands served", icon: Users },
  { value: "UK-wide", label: "Coverage", icon: MapPin },
];

const PROCESS = [
  { icon: PenLine, title: "Brief & script", body: "We take your brief on a short call, ideate together, and write a script that lands the message in the shortest time possible." },
  { icon: Clapperboard, title: "Pre-production", body: "Storyboards, shot lists, location recces, talent, kit and schedules — planned so filming day runs on rails." },
  { icon: Camera, title: "Filming", body: "A full crew arrives with broadcast-quality kit. On many shoots you also get FREE stills photography while we're on-site." },
  { icon: Scissors, title: "Edit & motion graphics", body: "Editing, colour, motion graphics, sound design and multiple cutdowns for socials, web and broadcast." },
  { icon: Send, title: "Delivery", body: "Files delivered in the formats you need, with unlimited copyright usage. Amends included until you're happy." },
];

const CREDENTIALS = [
  "CAA-licensed drone operator (A2 CofC & GVC held in-house)",
  "Broadcast-quality Sony FX & Canon cinema kit as standard",
  "Public Liability insurance up to £5m",
  "Registered UK Limited company (New Reality Media Ltd) since 2012",
  "GDPR-compliant workflow for interviews & personal footage",
];

// AboutPage + Person JSON-LD gives Google E-E-A-T signals.
const aboutJsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "@id": `${SITE_URL}/about#page`,
    url: `${SITE_URL}/about`,
    name: "About Yellow Video Production",
    description:
      "Yellow Video Production is a Liverpool-based video production company founded in 2012. Meet the team, learn our approach, and see why 1,000+ films have been delivered UK-wide.",
    mainEntity: { "@id": `${SITE_URL}/#organization` },
    isPartOf: { "@id": `${SITE_URL}/#website` },
    inLanguage: "en-GB",
  },
  ...TEAM.map((t) => ({
    "@context": "https://schema.org",
    "@type": "Person",
    name: t.name,
    jobTitle: t.role,
    description: t.bio,
    image: `${SITE_URL}${t.photo}`,
    worksFor: { "@id": `${SITE_URL}/#organization` },
    ...(t.linkedin ? { sameAs: [t.linkedin] } : {}),
  })),
];

export default function About() {
  return (
    <main className="pt-28 pb-12" data-testid="about-page">
      <SEO
        title="About Yellow | Liverpool Video Production Company Since 2012"
        description="Meet Yellow Video Production — Liverpool's video production company since 2012. 1,000+ films for brands, universities, charities & government. Meet the team, our process and credentials."
        path="/about"
        keywords="about yellow video production, liverpool video production company, video production team liverpool, dave knight yellow, meet the team video production, yellow video agency"
        jsonLd={aboutJsonLd}
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "About", path: "/about" },
        ]}
      />

      {/* Hero */}
      <section className="yv-container">
        <Reveal>
          <p className="yv-eyebrow">About Yellow · Since 2012</p>
          <h1 className="yv-h1 mt-3 max-w-5xl">
            Your new video department. <span style={{ color: "var(--yellow)" }}>Based in Liverpool</span> but covering the entire UK.
          </h1>
          <p className="text-white/70 max-w-3xl mt-6 text-lg leading-relaxed">
            Yellow Video Production is a full-service video production company in Liverpool. Since 2012 we've delivered over <strong className="text-white">1,000 films</strong> for global brands, universities, charities and government agencies — filmed, edited and delivered by one dedicated team.
          </p>
          <div className="flex flex-wrap gap-3 mt-8">
            <Link to="/contact" className="yv-btn yv-btn-primary" data-testid="about-cta-quote">Get a fixed-price quote</Link>
            <Link to="/showreel" className="yv-btn yv-btn-secondary" data-testid="about-cta-showreel">Watch the showreel</Link>
          </div>
        </Reveal>
      </section>

      <ClientMarquee />

      {/* Numbers strip */}
      <section className="yv-container !mt-16">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {NUMBERS.map((n, i) => {
            const Icon = n.icon;
            return (
              <Reveal key={n.label} delay={i * 80}>
                <div className="bg-white/[0.02] border border-white/10 rounded-md p-6 text-center" data-testid={`about-number-${n.label.toLowerCase().replace(/\s/g, '-')}`}>
                  <Icon size={22} className="text-[var(--yellow)] mx-auto mb-2" />
                  <p className="yv-h2 text-white mb-1">{n.value}</p>
                  <p className="yv-eyebrow text-[10px]">{n.label}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* Our story */}
      <section className="yv-container !mt-20 grid md:grid-cols-2 gap-12 items-start">
        <Reveal>
          <p className="yv-eyebrow">Our story</p>
          <h2 className="yv-h2 text-white mt-2 mb-6">Video production, done properly.</h2>
          <div className="text-white/70 leading-relaxed space-y-4">
            <p>
              Yellow was founded in 2012 by <strong className="text-white">Dave Roberts</strong>, <strong className="text-white">Shaun Bishop</strong>, <strong className="text-white">Keith Lupton</strong> and <strong className="text-white">Scott Bishop</strong> — former freelancers who came together to make life easier for clients looking for high-quality video content for marketing, training, internal comms and events.
            </p>
            <p>
              Twelve years and over 1,000 films later, we still work the same way: one crew from script to screen, one project manager who answers the phone, one fixed price with no agency markup, and one deadline we hit every time.
            </p>
            <p>
              From product films for <strong className="text-white">Aldi</strong> and <strong className="text-white">Persil</strong> to internal comms for the <strong className="text-white">NHS</strong> and <strong className="text-white">Co-op</strong>, brand campaigns for <strong className="text-white">Cancer Research UK</strong> and case-study films for universities, we've built a reputation for turning complicated briefs into cinema-quality output.
            </p>
          </div>
        </Reveal>
        <Reveal delay={120}>
          <div className="bg-white/[0.02] border border-white/10 rounded-md p-8">
            <Heart size={28} className="text-[var(--yellow)] mb-4" />
            <h3 className="yv-h3 text-white mb-3">What we believe</h3>
            <p className="text-white/70 text-sm leading-relaxed mb-4">
              The best video isn't the flashiest — it's the one that shifts an audience. We start every project by asking what the film needs to <em>do</em>, and design around that.
            </p>
            <ul className="space-y-2 text-white/75 text-sm">
              {[
                "One team, one fixed price, no agency middlemen",
                "Cinema-quality kit and crew as standard",
                "Unlimited copyright — you own every file forever",
                "FREE stills photography included on most shoots",
                "Quote in 4 working hours, first cut within 2 weeks",
              ].map((v) => (
                <li key={v} className="flex gap-2">
                  <CheckCircle2 size={16} className="text-[var(--yellow)] flex-shrink-0 mt-0.5" />
                  <span>{v}</span>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      </section>

      {/* Meet the team */}
      <section className="yv-container !mt-20">
        <Reveal>
          <p className="yv-eyebrow">Meet the team</p>
          <h2 className="yv-h2 text-white mt-2 max-w-3xl mb-10">The faces behind every Yellow film.</h2>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {TEAM.map((member, i) => (
            <Reveal key={member.name} delay={i * 100}>
              <article
                className="bg-white/[0.02] border border-white/10 rounded-md overflow-hidden h-full flex flex-col group"
                data-testid={`team-card-${member.slug}`}
              >
                <div className="aspect-[4/5] bg-[#0a0a0a] overflow-hidden relative">
                  <img
                    src={member.photo}
                    alt={`${member.name}, ${member.role} at Yellow Video Production`}
                    loading="lazy"
                    width={800}
                    height={1000}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent pointer-events-none" />
                  <div className="absolute bottom-4 left-4 right-4">
                    <p className="yv-eyebrow text-[var(--yellow)] text-[10px]">{member.lead}</p>
                  </div>
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <h3 className="yv-h3 text-white text-xl">{member.name}</h3>
                  <p className="yv-eyebrow text-[10px] mt-1 mb-4">{member.role}</p>
                  <p className="text-white/65 text-sm leading-relaxed flex-1">{member.bio}</p>
                  {member.linkedin && (
                    <a
                      href={member.linkedin}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1.5 text-[var(--yellow)] text-xs font-semibold mt-4 hover:underline"
                      data-testid={`team-linkedin-${member.slug}`}
                    >
                      Connect on LinkedIn →
                    </a>
                  )}
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Behind the scenes gallery */}
      <section className="yv-container !mt-24">
        <Reveal>
          <p className="yv-eyebrow">Behind the scenes</p>
          <h2 className="yv-h2 text-white mt-2 max-w-3xl mb-3">On set, in the edit, out on location.</h2>
          <p className="text-white/60 max-w-2xl mb-10 leading-relaxed">
            A peek at what a Yellow shoot day actually looks like — from product photography and interviews to charity conferences and theatre-stage lighting.
          </p>
        </Reveal>
        <BtsGallery />
      </section>

      {/* How we work / process */}
      <section className="yv-container !mt-24">
        <Reveal>
          <p className="yv-eyebrow">How we work</p>
          <h2 className="yv-h2 text-white mt-2 max-w-3xl mb-10">Five steps, one team, zero surprises.</h2>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {PROCESS.map((p, i) => {
            const Icon = p.icon;
            return (
              <Reveal key={p.title} delay={i * 80}>
                <div className="bg-white/[0.02] border border-white/10 rounded-md p-6 h-full">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="p-2 rounded-md bg-[var(--yellow)]/10 text-[var(--yellow)]">
                      <Icon size={18} />
                    </span>
                    <span className="text-white/40 font-mono text-xs">0{i + 1}</span>
                  </div>
                  <h3 className="text-white font-bold mb-2">{p.title}</h3>
                  <p className="text-white/60 text-sm leading-relaxed">{p.body}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* Credentials */}
      <section className="yv-container !mt-24 grid md:grid-cols-2 gap-12 items-start">
        <Reveal>
          <p className="yv-eyebrow">Credentials & compliance</p>
          <h2 className="yv-h2 text-white mt-2 mb-6">The paperwork's already sorted.</h2>
          <p className="text-white/70 leading-relaxed">
            Every shoot Yellow delivers is fully licensed, insured and legally clean. Our drone team holds current CAA certification, our kit list is broadcast-standard and our contracts protect your IP.
          </p>
        </Reveal>
        <Reveal delay={120}>
          <ul className="space-y-3">
            {CREDENTIALS.map((c) => (
              <li key={c} className="flex gap-3 text-white/80" data-testid="credential-item">
                <CheckCircle2 className="text-[var(--yellow)] flex-shrink-0 mt-0.5" size={20} />
                <span>{c}</span>
              </li>
            ))}
          </ul>
        </Reveal>
      </section>

      {/* Testimonial */}
      <section className="yv-container !mt-24">
        <Reveal>
          <p className="yv-eyebrow">In their words</p>
          <h2 className="yv-h2 text-white mt-2 mb-10 max-w-3xl">What clients say about working with us.</h2>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {TESTIMONIALS.map((t, i) => (
            <Reveal key={t.author} delay={i * 100}>
              <figure className="bg-white/[0.02] border border-white/10 rounded-md p-6 h-full">
                <blockquote className="text-white/80 leading-relaxed italic mb-6 text-sm">
                  "{t.quote}"
                </blockquote>
                <figcaption className="border-t border-white/10 pt-4">
                  <p className="text-white font-semibold text-sm">{t.author}</p>
                  <p className="yv-eyebrow text-[10px] mt-1">{t.company}</p>
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="yv-container !mt-24">
        <Reveal>
          <div className="bg-gradient-to-br from-[var(--yellow)]/10 to-transparent border border-[var(--yellow)]/30 rounded-md p-10 md:p-16 text-center">
            <p className="yv-eyebrow">Get in touch</p>
            <h2 className="yv-h2 text-white mt-2 mb-6 max-w-2xl mx-auto">
              Got a brief? We'll come back with a fixed price in 4 hours.
            </h2>
            <p className="text-white/70 max-w-2xl mx-auto mb-8">
              Call {SITE.phone}, email {SITE.email}, or send your brief through and we'll be in touch the same working day.
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <Link to="/contact" className="yv-btn yv-btn-primary" data-testid="about-footer-cta">Start a project</Link>
              <Link to="/video-production-prices" className="yv-btn yv-btn-secondary">See our prices</Link>
            </div>
          </div>
        </Reveal>
      </section>
    </main>
  );
}
